import React, { useContext } from 'react';
import styled from 'styled-components';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { CountryCode } from '@dayetopia/types';
import Colors from '@components/shared/Colors';
import { BodyLG, BodyMDBold, BodySM } from '@components/shared/TextTypes';
import TwoColumn from '@components/shared/TwoColumn';
import { VMS_SPECIALIST_CARE_IMG } from '@constants';
import { PageSlice } from '@contracts';
import HealthAssessments from '@images/HealthAssessments.svg';
import AppointmentsIcon from '@images/vms-aftercare-appointments.svg';
import HealthHacksIcon from '@images/vms-aftercare-hacks.svg';
import TreatmentsIcon from '@images/vms-aftercare-treatments.svg';
import { replaceText } from '@utils/handleTextReplacement';
import { Context } from '@components/Context';

interface Props {
  slice: PageSlice;
}

const iconData = [
  {
    key: 'components.vms-specialist-care.treatments',
    fees: true,
    icon: TreatmentsIcon,
    pounds: 5,
    coins: 99,
    iconWidth: 52,
    iconHeight: 64
  },
  {
    key: 'components.vms-specialist-care.appointments',
    fees: true,
    icon: AppointmentsIcon,
    pounds: 24,
    coins: 99,
    iconWidth: 48,
    iconHeight: 64
  },
  {
    key: 'components.vms-specialist-care.healthhacks',
    fees: false,
    icon: HealthHacksIcon,
    pounds: 0,
    coins: 0,
    iconWidth: 68,
    iconHeight: 65
  }
];

const VMSSpecialistCare: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { currency, country } = state;
  const location = useLocation();

  const renderPrice = (item) => {
    if (country != CountryCode.GB) {
      return <PriceWrap />;
    }

    return (
      <PriceWrap>
        {item.fees ? <PriceLabel>{t('components.starting-from')}</PriceLabel> : null}
        <PriceFormat>
          {item.fees ? (
            <>
              {currency.symbol} {item.pounds} <Superscript> {item.coins} </Superscript>
            </>
          ) : (
            <FreeLabel>{t('components.free')}</FreeLabel>
          )}
        </PriceFormat>
      </PriceWrap>
    );
  };

  const renderIconBox = (item) => (
    <IconBox key={item.key}>
      <BoxSizer width={item.iconWidth} height={item.iconHeight}>
        <item.icon />
      </BoxSizer>
      <TextPriceWrap>
        <IconTextWrap>
          <BodyMDBold>{replaceText(t(`${item.key}`), country, location)}</BodyMDBold>
        </IconTextWrap>
        {renderPrice(item)}
      </TextPriceWrap>
    </IconBox>
  );

  return (
    <Container>
      <Inner>
        <TwoColumn gap="70px" mobileReverse={false} breakpoint="1024px" alignment="center">
          <StyledImg src={VMS_SPECIALIST_CARE_IMG} alt="VMS Specialist Care" fetchpriority="low" width="848" height="1100" />
          <TextWrap>
            <Title>{replaceText(t('components.vms-specialist-care.title'), country, location)}</Title>
            <StyledBodyLG>{t('components.vms-specialist-care.description')}</StyledBodyLG>
            <IconBoxWrap>{iconData.map((item) => renderIconBox(item))}</IconBoxWrap>
            <TextAndIconContainer>
              <TextSizer>{t('components.vms-specialist-care.pdf')}</TextSizer>
              <HealthAssessments />
            </TextAndIconContainer>
            <StyledBodySM>{t('components.vms-specialist-care.note')}</StyledBodySM>
          </TextWrap>
        </TwoColumn>
      </Inner>
    </Container>
  );
};

export default VMSSpecialistCare;

const Container = styled.div`
  padding: 120px 0;

  @media (max-width: 1024px) {
    padding: 60px 30px 30px;
  }
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  max-width: 480px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    align-items: center;
  }
`;

const Title = styled.h3`
  font-family: 'Domaine';
  font-style: normal;
  font-weight: normal;
  font-size: 30px;
  line-height: 120%;
  color: inherit;

  @media (max-width: 1024px) {
    text-align: center;
    font-size: 24px;
  }
`;

const BoxSizer = styled.div<{ width: string; height: string }>`
  width: ${(p) => p.width};
  height: ${(p) => p.height};
`;

const IconBoxWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  margin-bottom: 10px;
  gap: 20px;
`;

const IconBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  padding: 20px 0 0;
  color: ${Colors.navyBlue};
  border-top: 1px solid ${Colors.ecru};

  &:first-child {
    border-top: none;
    padding-top: 0;
  }

  @media (max-width: 1024px) {
    align-items: center;
    padding: 20px 20px 0;

    &:first-child {
      padding-top: 20px;
    }
  }
`;

const IconTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 5px;

  & > span.fade {
    opacity: 0.5;
  }
`;

const TextPriceWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 5px;
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const PriceWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 3px;
  font-family: 'Domaine';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 110%;
  width: 72px;
  @media (max-width: 1024px) {
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
  }
`;

const PriceFormat = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-start;
  flex: none;
  gap: 2px;
  padding: 0;
  font-family: 'Domaine';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 110%;
  width: max-content;
`;

const FreeLabel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 5px 8px;
  gap: 10px;
  background: #eba920;
  border-radius: 2px;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 11px;
  line-height: 13px;
  text-align: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${Colors.cream};
`;

const PriceLabel = styled.div`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 100%;
  color: ${Colors.navyBlue};
  opacity: 0.5;
  @media (max-width: 1024px) {
    font-size: 13px;
    line-height: 150%;
  }
`;

const Superscript = styled.span`
  font-size: 12px;
  line-height: 110%;
  text-align: center;
`;

const StyledBodyLG = styled(BodyLG)`
  text-align: left;
  margin-bottom: 20px;
  font-size: 16px;
  color: inherit;

  @media (max-width: 1024px) {
    text-align: center;
    margin-bottom: 0;
  }
`;

const StyledBodySM = styled(BodySM)`
  @media (max-width: 1024px) {
    text-align: center;
  }
`;

const StyledImg = styled(LazyLoadImage)`
  width: 100%;
  max-width: 424px;
  height: auto;

  @media (max-width: 1024px) {
    margin-bottom: 40px;
  }
`;

const TextAndIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  margin-bottom: 20px;
  width: 100%;
  gap: 20px;
  background: #f1f6e4;
  border-radius: 2px;
`;

const TextSizer = styled.div`
  width: 292px;
  color: ${Colors.navyBlue};
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  @media (max-width: 1024px) {
    width: 205px;
    font-size: 14px;
    line-height: 140%;
  }
`;
